<template>
    <div class="recruits-page">
        <!-- 顶部横幅 -->
        <PageHeader
            :title="$t('recruits.joinTitle')"
            :subtitle="$t('recruits.headerSubtitle')"
            :breadcrumbItems="[{ text: $t('common.home'), path: $lp('/') }]"
            :currentText="$t('routes.recruits')"
        />
        <!-- 公司文化介绍 -->
        <section class="company-culture">
            <div class="page-container">
                <div class="culture-content">
                    <div class="culture-image">
                        <img :src="require('@/assets/images/about/r01.jpg')" :alt="$t('recruits.cultureTitle')">
                    </div>
                    <div class="culture-text">
                        <h2>{{ $t('recruits.cultureTitle') }}</h2>
                        <p>{{ $t('recruits.cultureDesc') }}</p>
                        <div class="culture-values">
                            <div class="value-item">
                                <i class="el-icon-star-on"></i>
                                <h3>{{ $t('recruits.value1Title') }}</h3>
                                <p>{{ $t('recruits.value1Desc') }}</p>
                            </div>
                            <div class="value-item">
                                <i class="el-icon-s-cooperation"></i>
                                <h3>{{ $t('recruits.value2Title') }}</h3>
                                <p>{{ $t('recruits.value2Desc') }}</p>
                            </div>
                            <div class="value-item">
                                <i class="el-icon-s-flag"></i>
                                <h3>{{ $t('recruits.value3Title') }}</h3>
                                <p>{{ $t('recruits.value3Desc') }}</p>
                            </div>
                            <div class="value-item">
                                <i class="el-icon-s-claim"></i>
                                <h3>{{ $t('recruits.value4Title') }}</h3>
                                <p>{{ $t('recruits.value4Desc') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 职位类别（静态，docx 内容呈现） -->
        <section class="job-categories-section">
            <div class="page-container">
                <div class="section-header text-center">
                    <h2 class="section-title">{{ $t('recruits.categoriesTitle') }}</h2>
                    <p class="section-desc">{{ $t('recruits.categoriesDesc') }}</p>
                </div>

                <div class="category-grid">
                    <article
                        v-for="cat in jobCategories"
                        :key="cat.id"
                        class="category-card"
                        @click="goToCategory(cat.id)"
                        role="button"
                        tabindex="0"
                        @keydown.enter="goToCategory(cat.id)"
                    >
                        <div class="category-icon">{{ cat.icon }}</div>
                        <h3 class="category-title">{{ cat.title }}</h3>
                        <p class="category-desc">{{ cat.desc }}</p>
                        <div class="category-positions">
                            <span class="positions-label">{{ $t('recruits.positionsLabel') }}</span>
                            <ul class="position-chips">
                                <li
                                    v-for="(pos, idx) in cat.positions.slice(0, 5)"
                                    :key="idx"
                                    class="position-chip"
                                >
                                    {{ pos }}
                                </li>
                                <li v-if="cat.positions.length > 5" class="position-chip position-chip--more">
                                    +{{ cat.positions.length - 5 }}
                                </li>
                            </ul>
                        </div>
                        <span class="category-cta">{{ $t('common.viewDetail') }} →</span>
                    </article>
                </div>
            </div>
        </section>

        <!-- 福利待遇 -->
        <section class="job-benefits">
            <div class="page-container">
                <div class="section-header text-center">
                    <h2 class="section-title">{{ $t('recruits.whyJoinTitle') }}</h2>
                    <p class="section-desc">{{ $t('recruits.whyJoinDesc') }}</p>
                </div>
                
                <div class="benefits-container">
                    <div class="benefit-item">
                        <i class="el-icon-medal"></i>
                        <h3>{{ $t('recruits.benefit1Title') }}</h3>
                        <p>{{ $t('recruits.benefit1Desc') }}</p>
                    </div>
                    <div class="benefit-item">
                        <i class="el-icon-first-aid-kit"></i>
                        <h3>{{ $t('recruits.benefit2Title') }}</h3>
                        <p>{{ $t('recruits.benefit2Desc') }}</p>
                    </div>
                    <div class="benefit-item">
                        <i class="el-icon-sunrise"></i>
                        <h3>{{ $t('recruits.benefit3Title') }}</h3>
                        <p>{{ $t('recruits.benefit3Desc') }}</p>
                    </div>
                    <div class="benefit-item">
                        <i class="el-icon-reading"></i>
                        <h3>{{ $t('recruits.benefit4Title') }}</h3>
                        <p>{{ $t('recruits.benefit4Desc') }}</p>
                    </div>
                    <div class="benefit-item">
                        <i class="el-icon-dessert"></i>
                        <h3>{{ $t('recruits.benefit5Title') }}</h3>
                        <p>{{ $t('recruits.benefit5Desc') }}</p>
                    </div>
                    <div class="benefit-item">
                        <i class="el-icon-trophy"></i>
                        <h3>{{ $t('recruits.benefit6Title') }}</h3>
                        <p>{{ $t('recruits.benefit6Desc') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- 申请流程 -->
        <section class="application-process">
            <div class="page-container">
                <div class="section-header text-center">
                    <h2 class="section-title">{{ $t('recruits.processTitle') }}</h2>
                    <p class="section-desc">{{ $t('recruits.processDesc') }}</p>
                </div>
                
                <div class="process-steps">
                    <div class="process-step">
                        <div class="step-number">1</div>
                        <div class="step-info">
                            <h3>{{ $t('recruits.step1Title') }}</h3>
                            <p>{{ $t('recruits.step1Desc') }}</p>
                        </div>
                    </div>
                    <div class="process-step">
                        <div class="step-number">2</div>
                        <div class="step-info">
                            <h3>{{ $t('recruits.step2Title') }}</h3>
                            <p>{{ $t('recruits.step2Desc') }}</p>
                        </div>
                    </div>
                    <div class="process-step">
                        <div class="step-number">3</div>
                        <div class="step-info">
                            <h3>{{ $t('recruits.step3Title') }}</h3>
                            <p>{{ $t('recruits.step3Desc') }}</p>
                        </div>
                    </div>
                    <div class="process-step">
                        <div class="step-number">4</div>
                        <div class="step-info">
                            <h3>{{ $t('recruits.step4Title') }}</h3>
                            <p>{{ $t('recruits.step4Desc') }}</p>
                        </div>
                    </div>
                    <div class="process-step">
                        <div class="step-number">5</div>
                        <div class="step-info">
                            <h3>{{ $t('recruits.step5Title') }}</h3>
                            <p>{{ $t('recruits.step5Desc') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 联系方式 -->
        <section class="contact-section">
            <div class="page-container">
                <div class="contact-info">
                    <h2>{{ $t('routes.touch') }}</h2>
                    <p>{{ $t('recruits.contactDesc') }}</p>
                    <div class="contact-methods">
                        <div class="contact-method">
                            <i class="el-icon-message"></i>
                            <span>{{ $t('recruits.contactEmailLabel') }}jtx@jingtianxia.com</span>
                        </div>
                        <div class="contact-method">
                            <i class="el-icon-phone"></i>
                            <span>{{ $t('recruits.contactHotlineLabel') }}010-89757238</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/PageHeader.vue';
import { localePath } from '@/utils/locale';

// 国际化
const { t, locale } = useI18n();
const router = useRouter();

// 六大职位类别（静态内容，docx 提取；与 JobCategoryDetail 共享 id 命名）
// 涵盖岗位使用 `;` 分隔，运行时拆分为列表
const jobCategories = computed(() => {
  const raw = [
    { id: 'rd',        icon: '🧪', titleKey: 'recruits.jobRdTitle',        descKey: 'recruits.jobRdDesc',        positionsKey: 'recruits.jobRdPositions' },
    { id: 'landscape', icon: '🌿', titleKey: 'recruits.jobLandscapeTitle', descKey: 'recruits.jobLandscapeDesc', positionsKey: 'recruits.jobLandscapePositions' },
    { id: 'solution',  icon: '📡', titleKey: 'recruits.jobSolutionTitle',  descKey: 'recruits.jobSolutionDesc',  positionsKey: 'recruits.jobSolutionPositions' },
    { id: 'business',  icon: '🤝', titleKey: 'recruits.jobBusinessTitle',  descKey: 'recruits.jobBusinessDesc',  positionsKey: 'recruits.jobBusinessPositions' },
    { id: 'function',  icon: '📋', titleKey: 'recruits.jobFunctionTitle',  descKey: 'recruits.jobFunctionDesc',  positionsKey: 'recruits.jobFunctionPositions' },
    { id: 'intern',    icon: '🎓', titleKey: 'recruits.jobInternTitle',    descKey: 'recruits.jobInternDesc',    positionsKey: 'recruits.jobInternPositions' }
  ];
  return raw.map(item => ({
    id: item.id,
    icon: item.icon,
    title: t(item.titleKey),
    desc: t(item.descKey),
    positions: t(item.positionsKey).split(';').map(s => s.trim()).filter(Boolean)
  }));
});

const goToCategory = (id) => {
  router.push(localePath(`/recruits/jobs/${id}`, locale.value));
};

</script>

<style lang="scss" scoped>
.recruits-page {
    padding-bottom: 60px;
    
    .company-culture {
        padding: 80px 0;
        background-color: #fff;
        
        .culture-content {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 50px;
            align-items: center;
            
            @media (max-width: 992px) {
                grid-template-columns: 1fr;
            }
            
            .culture-image {
                height: 400px;
                border-radius: 10px;
                overflow: hidden;
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                
                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }
            
            .culture-text {
                h2 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #333;
                    margin-bottom: 20px;
                }
                
                p {
                    font-size: 16px;
                    color: #666;
                    line-height: 1.7;
                    margin-bottom: 30px;
                }
                
                .culture-values {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 20px;
                    
                    .value-item {
                        padding: 20px;
                        background-color: #f9f9f9;
                        border-radius: 8px;
                        transition: transform 0.3s ease;
                        
                        &:hover {
                            transform: translateY(-5px);
                            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
                        }
                        
                        i {
                            font-size: 30px;
                            color: #409EFF;
                            margin-bottom: 10px;
                        }
                        
                        h3 {
                            font-size: 18px;
                            font-weight: 600;
                            color: #333;
                            margin-bottom: 10px;
                        }
                        
                        p {
                            font-size: 14px;
                            color: #666;
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }
    }
    
    .job-categories-section {
        padding: 80px 0 30px;
        background-color: #fff;

        .category-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
            margin-top: 40px;

            @media (max-width: 992px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (max-width: 576px) {
                grid-template-columns: 1fr;
            }
        }

        .category-card {
            display: flex;
            flex-direction: column;
            padding: 32px 28px;
            background: #fafbfa;
            border: 1px solid rgba(23, 56, 42, 0.08);
            border-top: 4px solid #3d8b57;
            cursor: pointer;
            transition: transform 0.25s, box-shadow 0.25s, border-top-color 0.25s;
            outline: none;

            &:hover,
            &:focus {
                transform: translateY(-4px);
                box-shadow: 0 18px 40px -22px rgba(23, 56, 42, 0.4);
                border-top-color: #17382a;

                .category-cta {
                    color: #3d8b57;
                    letter-spacing: 2px;
                }
            }

            .category-icon {
                font-size: 36px;
                line-height: 1;
                margin-bottom: 16px;
            }

            .category-title {
                font-size: 19px;
                font-weight: 700;
                color: #17382a;
                margin: 0 0 12px;
                line-height: 1.4;
            }

            .category-desc {
                font-size: 14.5px;
                line-height: 1.85;
                color: #4a5a52;
                margin: 0 0 20px;
                text-align: justify;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            .category-positions {
                margin-top: auto;

                .positions-label {
                    display: block;
                    font-size: 12px;
                    letter-spacing: 2px;
                    color: #6b8f71;
                    margin-bottom: 10px;
                }

                .position-chips {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    list-style: none;
                    padding: 0;
                    margin: 0 0 18px;

                    .position-chip {
                        font-size: 12.5px;
                        padding: 4px 10px;
                        background: #fff;
                        color: #26302b;
                        border: 1px solid rgba(23, 56, 42, 0.12);
                        white-space: nowrap;

                        &--more {
                            background: #f1f6f2;
                            color: #3d8b57;
                            font-weight: 600;
                            border-color: rgba(61, 139, 87, 0.3);
                        }
                    }
                }
            }

            .category-cta {
                font-size: 13px;
                letter-spacing: 1px;
                color: #17382a;
                font-weight: 600;
                transition: color 0.25s, letter-spacing 0.25s;
            }
        }
    }

    .job-benefits {
        padding: 80px 0;
        background-color: #f5f7fa;
        
        .benefits-container {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;
            margin-top: 50px;
            
            @media (max-width: 992px) {
                grid-template-columns: repeat(2, 1fr);
            }
            
            @media (max-width: 576px) {
                grid-template-columns: 1fr;
            }
            
            .benefit-item {
                background-color: #fff;
                padding: 30px;
                border-radius: 8px;
                text-align: center;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                transition: transform 0.3s ease;
                
                &:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
                }
                
                i {
                    font-size: 36px;
                    color: #409EFF;
                    margin-bottom: 15px;
                    display: block;
                }
                
                h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                    margin-bottom: 10px;
                }
                
                p {
                    color: #666;
                    font-size: 14px;
                    line-height: 1.6;
                }
            }
        }
    }
    
    .application-process {
        padding: 80px 0;
        
        .process-steps {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
            position: relative;
            
            &:before {
                content: '';
                position: absolute;
                top: 40px;
                left: 70px;
                right: 70px;
                height: 3px;
                background-color: #e4e7ed;
                z-index: 0;
            }
            
            @media (max-width: 992px) {
                flex-direction: column;
                gap: 30px;
                
                &:before {
                    display: none;
                }
            }
            
            .process-step {
                display: flex;
                flex-direction: column;
                align-items: center;
                position: relative;
                z-index: 1;
                flex: 1;
                
                @media (max-width: 992px) {
                    flex-direction: row;
                    gap: 20px;
                }
                
                .step-number {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background-color: #409EFF;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                    font-weight: 700;
                    margin-bottom: 20px;
                    box-shadow: 0 5px 15px rgba(64, 158, 255, 0.3);
                }
                
                .step-info {
                    text-align: center;
                    
                    @media (max-width: 992px) {
                        text-align: left;
                    }
                    
                    h3 {
                        font-size: 18px;
                        font-weight: 600;
                        color: #333;
                        margin-bottom: 5px;
                    }
                    
                    p {
                        color: #666;
                        font-size: 14px;
                    }
                }
            }
        }
    }
    
    .contact-section {
        padding: 60px 0;
        background-color: #f5f7fa;
        
        .contact-info {
            max-width: 600px;
            margin: 0 auto;
            text-align: center;
            
            h2 {
                font-size: 28px;
                font-weight: 600;
                color: #333;
                margin-bottom: 15px;
            }
            
            p {
                color: #666;
                font-size: 16px;
                margin-bottom: 30px;
            }
            
            .contact-methods {
                display: flex;
                flex-direction: column;
                gap: 15px;
                
                .contact-method {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    
                    i {
                        font-size: 20px;
                        color: #409EFF;
                    }
                    
                    span {
                        font-size: 16px;
                        color: #333;
                    }
                }
            }
        }
    }
}

.section-header {
    margin-bottom: 30px;
    
    .section-title {
        font-size: 32px;
        font-weight: 700;
        color: #333;
        margin-bottom: 15px;
    }
    
    .section-desc {
        font-size: 16px;
        color: #666;
        max-width: 700px;
        margin: 0 auto;
    }
    
    &.text-center {
        text-align: center;
    }
}

.page-container {
    max-width: 1200px;
    padding: 0 15px;
    margin: 0 auto;
}
</style>